import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CompanyFraudRiskScoreEntity } from '@nk/data-entities-lib';
import { ErrorAlertComponent, isHttpErrorResponse, TranslateFallbackPipe } from '@nk/ui-lib';
import { catchError, map, Observable, of, startWith } from 'rxjs';
import { FraudScoreRiskColorClassPipe } from '../../pipes/fraud-score-risk-color-class.pipe';
import { FraudRiskScoreService } from '../../services/fraud-risk-score.service';
import { FRAUD_RISK_SCORE_COLOR, FRAUD_RISK_SCORE_TRANSLATIONS } from '../../utils/fraud-risk-score.utils';
import { CompanyAnalysisRiskLayoutComponent } from '../company-analysis-risk-layout/company-analysis-risk-layout.component';
import { CompanyAnalysisRiskLoadingComponent } from '../company-analysis-risk-loading/company-analysis-risk-loading.component';
import { RiskLevelIconComponent } from '../risk-level-icon/risk-level-icon.component';

interface FraudRiskScoreState {
  loading: boolean;
  data: CompanyFraudRiskScoreEntity | null;
}

@Component({
  selector: 'nk-company-analysis-fraud-risk-score',
  imports: [
    AsyncPipe,
    TranslateModule,
    TranslateFallbackPipe,
    RiskLevelIconComponent,
    CompanyAnalysisRiskLoadingComponent,
    CompanyAnalysisRiskLayoutComponent,
    FraudScoreRiskColorClassPipe,
    ErrorAlertComponent,
  ],
  templateUrl: './company-analysis-fraud-risk-score.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyAnalysisFraudRiskScoreComponent implements OnInit {
  public companyIdentifier = input.required<string>();
  public printVersion = input(false);
  public companyFraudRiskScoreState$!: Observable<FraudRiskScoreState>;
  public companyFraudRiskScoreTranslations = FRAUD_RISK_SCORE_TRANSLATIONS;
  public companyFraudRiskScoreColor = FRAUD_RISK_SCORE_COLOR;
  public error!: HttpErrorResponse;

  constructor(private _fraudRiskScoreService: FraudRiskScoreService) {}

  public ngOnInit(): void {
    this.companyFraudRiskScoreState$ = this._fraudRiskScoreService.companyFraudRiskScoreListCache
      .get(this.companyIdentifier())
      .pipe(
        map((data): FraudRiskScoreState => {
          const fraudRiskScore = data[0];
          if (!fraudRiskScore) {
            return { loading: false, data: null };
          }
          fraudRiskScore.fraud_risk_components.sort((a, b) => b.component_score - a.component_score);
          return { loading: false, data: fraudRiskScore };
        }),
        startWith({ loading: true, data: null }),
        catchError((error: unknown) => {
          if (isHttpErrorResponse(error)) {
            this.error = error;
          }
          return of({ loading: false, data: null });
        }),
      );
  }
}
